import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import ToolBox from '../../components/elements/ToolBox';
import DataTables from '../../components/elements/DataTables';
import { renderDate } from '../../utilities/renderDate';
import CreateMasterCategory from './CreatePointManagement';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import {
  deletePointManagement,
  editPointManagement,
  getLevel,
  getPointManagement,
  postPointManagement
} from '../../services/pointManagement';
import ModalDelete from '../../components/fragments/ModalDelete/ModalDelete';

const columns = [
  {
    heading: 'Level',
    props: 'levelName',
    id: 'levelName',
    sortable: true
  },
  {
    heading: 'Multiplier Point',
    props: 'multiplier',
    id: 'multiplier',
    sortable: true
  },
  {
    heading: 'Start Date',
    props: (item) => renderLocalTimeStartDate(item),
    id: 'startDate',
    sortable: true
  },
  {
    heading: 'End Date',
    props: (item) => renderLocalTimeEndDate(item),
    id: 'endDate',
    sortable: true
  },
  {
    heading: 'Action',
    props: 'action',
    sortable: false
  }
];

const renderDateFormat = (item) => renderDate(item.dateIn, 'datetimeyear');

const renderLocalTimeStartDate = (item) => {
  return <span>{convertToLocalTime(item.startDate + 'Z')}</span>;
};

const renderLocalTimeEndDate = (item) => {
  return <span>{convertToLocalTime(item.endDate + 'Z')}</span>;
};

const PointManagementPage = (props) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: ''
  });

  const [filter, setFilter] = useState({
    search: '',
    page: 1,
    size: 10
  });

  // Queries
  const fetchPointManagement = useQuery({
    queryKey: ['getPointManagement'],
    queryFn: () =>
      getPointManagement({
        search: filter.search,
        page: filter.page,
        size: filter.size
      })
  });

  const createPointManagement = useMutation({
    mutationFn: postPointManagement,
    onSuccess: () => {
      fetchPointManagement.refetch();
      handleCloseModal();
      showNotification({
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const updatePointManagement = useMutation({
    mutationFn: editPointManagement,
    onSuccess: () => {
      fetchPointManagement.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Update Point Management',
        message: 'Updated data will be show in the table'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const removePointManagement = useMutation({
    mutationFn: deletePointManagement,
    onSuccess: () => {
      fetchPointManagement.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Delete Point Management',
        message: 'Delete Point Management success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  useEffect(() => {
    if (filter?.search || filter?.page || filter?.size) {
      fetchPointManagement.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (paramsObject?.search || paramsObject?.page || paramsObject?.size) {
      setFilter({
        search: paramsObject?.search || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [paramsObject?.search, paramsObject?.page, paramsObject?.size]);

  const buttonOnClick = () => {
    setModalConfig({
      show: true,
      data: null,
      type: 'add'
    });
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/point-management',
      search: `?search=${data}&page=1`
    });
  }, []);

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: ''
    });
  };

  const handleDeleteData = (data) => {
    removePointManagement.mutate(data);
  };

  const handleSubmitData = (data) => {
    const payload = {
      ...data,
      userIn:
        modalConfig.type === 'edit'
          ? modalConfig.data.userIn
          : props.auth.binusianId,
      userUp: modalConfig.type === 'edit' ? props.auth.binusianId : ''
    };

    if (modalConfig.type === 'edit') {
      updatePointManagement.mutate({
        ...payload,
        pointID: modalConfig.data.pointID
      });
      return;
    }
    createPointManagement.mutate(payload);
    return;
  };

  const fetchedGetLevel = useQuery({
    queryKey: ['getLevel'],
    queryFn: () => getLevel({})
  });

  return (
    <AdminLayout>
      <Breadcrumbs
        pageTitle="Point Management"
        itemList={[
          {
            title: 'Back Office BCL&D',
            href: '#',
            icon: <Unicons.UilFileCopyAlt size={16} />
          },
          {
            title: 'Reward & Gamification',
            href: '#'
          },
          {
            title: 'Point Management',
            href: '/management/point-management'
          }
        ]}
      />
      <Card>
        <ToolBox
          handleSearch={handleSearch}
          buttonOnClick={buttonOnClick}
          buttonText="Create Point Management"
        />
        <DataTables
          columns={columns}
          loading={fetchPointManagement?.isLoading}
          data={fetchPointManagement?.data?.data?.data || []}
          handleDelete={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'delete'
            });
          }}
          handleEdit={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'edit'
            });
          }}
        />
      </Card>

      <CreateMasterCategory
        isVisible={
          modalConfig.show &&
          (modalConfig.type === 'add' || modalConfig.type === 'edit')
        }
        listLevel={fetchedGetLevel?.data?.data?.data || []}
        type={modalConfig.type}
        data={modalConfig.data}
        onClose={handleCloseModal}
        onSubmit={handleSubmitData}
        loading={createPointManagement?.isLoading}
      />

      <ModalDelete
        title="Point Management"
        isVisible={modalConfig.show && modalConfig.type === 'delete'}
        loading={removePointManagement.isLoading}
        onClose={handleCloseModal}
        onConfirmation={() =>
          handleDeleteData({
            pointID: modalConfig.data.pointID,
            userUp: props.auth.binusianId
          })
        }
      />
    </AdminLayout>
  );
};

function convertToLocalTime(dateString) {
  const date = new Date(dateString);
  const localDate = date.toLocaleString();
  return localDate;
}

export default PointManagementPage;
